import React, { createContext, useContext, useState, ReactNode } from 'react';

/**
 * Room関連の状態管理用Context
 * ゲストログイン後にroomidを保持し、アプリ全体で利用可能にする
 */

interface RoomContextType {
  roomId: string | null;
  setRoomId: (roomId: string | null) => void;
  clearRoom: () => void;
  isRoomSet: boolean;
}

interface RoomProviderProps {
  children: ReactNode;
}

// Context作成
const RoomContext = createContext<RoomContextType | undefined>(undefined);

/**
 * RoomProvider コンポーネント
 * アプリケーション全体でroomidの状態を管理
 */
export const RoomProvider: React.FC<RoomProviderProps> = ({ children }) => {
  const [roomId, setRoomIdState] = useState<string | null>(null);

  /**
   * roomidを設定
   */
  const setRoomId = (newRoomId: string | null) => {
    setRoomIdState(newRoomId);
    
    // ローカルストレージにも保存（ページリロード時の復元用）
    if (newRoomId) {
      localStorage.setItem('currentRoomId', newRoomId);
    } else {
      localStorage.removeItem('currentRoomId');
    }
  };

  /**
   * room情報をクリア
   */
  const clearRoom = () => {
    setRoomIdState(null);
    localStorage.removeItem('currentRoomId');
  };

  /**
   * roomが設定されているかチェック
   */
  const isRoomSet = Boolean(roomId);

  const value: RoomContextType = {
    roomId,
    setRoomId,
    clearRoom,
    isRoomSet,
  };

  return (
    <RoomContext.Provider value={value}>
      {children}
    </RoomContext.Provider>
  );
};

/**
 * RoomContextを使用するためのカスタムフック
 */
export const useRoom = (): RoomContextType => {
  const context = useContext(RoomContext);
  
  if (context === undefined) {
    throw new Error('useRoom must be used within a RoomProvider');
  }
  
  return context;
};

/**
 * roomidが設定されているかチェックするヘルパーフック
 */
export const useRequireRoom = (): { roomId: string; isRoomSet: true } => {
  const { roomId, isRoomSet } = useRoom();
  
  if (!isRoomSet || !roomId) {
    throw new Error('Room ID is required but not set');
  }
  
  return { roomId, isRoomSet: true };
};